import dayjs from 'src/utils/dayjs'
import type { SubscriptionState } from './subscriptionStateUtils'

export const getMembershipExpireTime = (
  expireAt: string | Date | dayjs.Dayjs | null | undefined,
) => {
  if (expireAt === null || expireAt === undefined) return undefined
  return dayjs(expireAt).utc().add(1, 'day').startOf('day')
}

export const shouldShowMembershipCountdown = (
  expireAt: dayjs.Dayjs,
  maximumLeftDays: number,
) => {
  const now = dayjs()

  return (
    expireAt.isValid() &&
    expireAt.isAfter(now) &&
    expireAt.diff(now, 'day') < maximumLeftDays
  )
}

export enum PaymentProvider {
  Google = 'google',
  Apple = 'apple',
  Onerway = 'onerway',
  Paypay = 'paypay',
  Other = 'other',
}

export const PaymentProviders = Object.values(PaymentProvider)

export type MembershipExpireDialogState =
  | {
      type: 'near-expire'
      paymentProvider: PaymentProvider
      days: number
    }
  | {
      type: 'expired'
      days: number
    }
  | {
      type: 'none'
    }

export type ActiveMembershipExpireDialogState = Exclude<
  MembershipExpireDialogState,
  { type: 'none' }
>

export const useMembershipExpireDialogState = (
  days: {
    nearExpire: number[]
    expired: number[]
  },
  state: SubscriptionState | undefined,
): MembershipExpireDialogState => {
  switch (state?.state) {
    case 'active': {
      const noticeDays = days.nearExpire.filter(d => d >= 0)

      const expire = getMembershipExpireTime(state?.current.endAt)
      if (!expire) {
        return {
          type: 'none',
        }
      }

      const now = dayjs().utc()
      const duration = expire.diff(now, 'day')

      const matchedDay = noticeDays.find(d => d === duration)

      const shouldShow =
        state.current.autoRenewable !== 'enabled' && // The subscription has auto-renewal enabled
        matchedDay !== undefined && // The current day matches one of the notice days
        expire.isValid() && // The expire date is valid
        expire.isAfter(now) // The expire date is after the current date

      if (!shouldShow) {
        return {
          type: 'none',
        }
      }
      return {
        type: 'near-expire',
        paymentProvider: state.current.paymentProvider,
        days: matchedDay,
      }
    }
    case 'expired': {
      // Flip the sign of the days to match the expected format
      const noticeDays = days.expired.filter(d => d >= 0).map(d => -d)

      const expire = getMembershipExpireTime(state?.previous.endAt)
      if (!expire) {
        return {
          type: 'none',
        }
      }

      const now = dayjs().utc()
      const duration = expire.diff(now, 'day') // Will be negative or zero
      const matchedDay = noticeDays.find(d => d === duration)

      const shouldShow =
        matchedDay !== undefined && // The current day matches one of the notice days
        expire.isValid() && // The expire date is valid
        expire.isBefore(now) // The expire date is after the current date

      if (!shouldShow) {
        return {
          type: 'none',
        }
      }

      return {
        type: 'expired',
        days: -matchedDay, // Return the positive value
      }
    }
    default: {
      return {
        type: 'none',
      }
    }
  }
}
